import { useMemo, useState } from 'react'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import Slider from '@/components/ui/Slider'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

const faqItems = [
  { question: 'How many months of expenses should I save?', answer: 'Most financial planners recommend 3-6 months of essential expenses. Freelancers or single-income households should aim for 6-9 months.' },
  { question: 'Where should I keep my emergency fund?', answer: 'Keep it in a high-yield savings account that is liquid and FDIC-insured, separate from your everyday checking account.' },
  { question: 'Does emergency fund include discretionary spending?', answer: 'No, only essential expenses like housing, utilities, groceries, insurance, and minimum debt payments should be included in your emergency fund target.' },
]

function EmergencyFund() {
  const [monthlyExpenses, setMonthlyExpenses] = useState(3200)
  const [currentSavings, setCurrentSavings] = useState(2500)
  const [monthsTarget, setMonthsTarget] = useState(6)
  const [monthlyContribution, setMonthlyContribution] = useState(300)

  const results = useMemo(() => {
    const targetAmount = monthlyExpenses * monthsTarget
    const gap = Math.max(0, targetAmount - currentSavings)
    const monthsToGoal = monthlyContribution > 0 ? Math.ceil(gap / monthlyContribution) : Infinity
    const coverageMonths = monthlyExpenses > 0 ? currentSavings / monthlyExpenses : 0
    return { targetAmount, gap, monthsToGoal, coverageMonths }
  }, [monthlyExpenses, currentSavings, monthsTarget, monthlyContribution])

  return (
    <>
      <Seo
        title="Emergency Fund Calculator - How Many Months Should You Save?"
        description="Free emergency fund calculator. Find your ideal 3-6 month savings buffer target and how long it will take to reach it based on your monthly contributions."
        path="/calculators/emergency-fund"
        schema={[
          softwareApplicationSchema('Emergency Fund Calculator', 'Calculate your ideal emergency fund target and time to reach it.', 'https://www.mintcalculator.com/calculators/emergency-fund'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Emergency Fund Calculator', path: '/calculators/emergency-fund' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Emergency Fund Calculator', path: '/calculators/emergency-fund' }]} />
        <SectionHeading
          eyebrow="Personal Budgeting & Net Worth"
          title="Emergency Fund Calculator"
          description="Find your ideal 3-6 month safety net buffer and see how long it will take to reach your goal."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-3">
            <NumberInput label="Essential monthly expenses" value={monthlyExpenses} onChange={setMonthlyExpenses} prefix="$" step={100} />
            <NumberInput label="Current emergency savings" value={currentSavings} onChange={setCurrentSavings} prefix="$" step={100} />
            <Slider label="Target buffer (months)" value={monthsTarget} onChange={setMonthsTarget} min={3} max={9} step={1} formatValue={v => `${v} mo`} />
            <NumberInput label="Monthly contribution toward goal" value={monthlyContribution} onChange={setMonthlyContribution} prefix="$" step={25} />
          </div>

          <div className="space-y-6 lg:col-span-2">
            <StatCard label="Target Emergency Fund" value={formatCurrency(results.targetAmount)} accent="emerald" size="lg" sublabel={`${monthsTarget} months of essentials`} />
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Amount Still Needed" value={formatCurrency(results.gap)} accent="amber" />
              <StatCard label="Current Coverage" value={`${results.coverageMonths.toFixed(1)} mo`} accent="cyan" />
            </div>
            <StatCard
              label="Time to Reach Goal"
              value={Number.isFinite(results.monthsToGoal) ? `${results.monthsToGoal} months` : 'N/A'}
              accent="teal"
              sublabel={Number.isFinite(results.monthsToGoal) ? `At ${formatCurrency(monthlyContribution)}/mo` : 'Increase your monthly contribution'}
            />
            <DisclaimerNote />
          </div>
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default EmergencyFund
